import { useFieldArray } from 'react-hook-form';
import { FC, useEffect } from 'react';
import { TradingFormField } from './trading-form-field';
import AddIcon from '@mui/icons-material/Add';
import { FormArrayItem } from '../../foundation/form/form-array-item';
import { useTradingFormContext } from './trading-form-context';
import { FormContainer } from '../../foundation/layout/form-container';
import { BaseWrapper } from '../../foundation/layout/field-wrapper';
import { TakeProfitLevelInput } from '@server/data-feed/schemas/input.schema';
import { useTemplates } from '@client/features/trading-templates/create-template/templates.hook';
import { Section } from '@client/foundation/layout/section';
import {
  borderStyle,
  sectionHeaderPanelStyle,
  sectionPanelStyle
} from '@client/foundation/layout/styling';
import { AppIconButton } from '@client/foundation/design-system/components';
import { createScaleInConfig } from './position-management-config.utils';

type PositionManagementConfigFormProps = {};

export const PositionManagementConfigForm: FC<
  PositionManagementConfigFormProps
> = () => {
  const { watch, setValue } = useTradingFormContext();

  const { marketConfig, positionManagementConfig } = watch();

  useTemplates({
    type: 'positionManagement',
    path: 'positionManagementConfig'
  });

  const { tradingMode } = marketConfig || {};
  const {
    stopLoss,
    trailingStop,
    scalping,
    scaleIn = createScaleInConfig()
  } =
    positionManagementConfig || {};
  const isScaleInEnabled = Boolean(scaleIn.enabled);

  useEffect(() => {
    if (!positionManagementConfig) {
      return;
    }

    if (!positionManagementConfig.scaleIn) {
      setValue(
        'positionManagementConfig.scaleIn',
        createScaleInConfig(),
        { shouldDirty: false, shouldTouch: false }
      );
    }
  }, [positionManagementConfig, setValue]);

  return (
    <>
      <FormContainer>
        {/* Position Size */}
        <TradingFormField
          type="numeric"
          name="positionManagementConfig.positionSize"
          label="Position Size"
        />

        {/* Leverage */}
        <TradingFormField
          type="numeric"
          name="positionManagementConfig.leverage"
          label="Leverage"
          inputProps={{
            min: 1,
            max: 20,
            step: 1
          }}
        />

        {/* Commission Percent */}
        {tradingMode === 'BACKTESTING' && (
          <TradingFormField
            type="numeric"
            name="positionManagementConfig.commissionPercent"
            label="Commission Percent"
            inputProps={{
              min: 0,
              step: 0.001
            }}
          />
        )}

        {/* Stop Loss */}
        <TradingFormField
          type="checkbox"
          name="positionManagementConfig.stopLoss"
          label="Stop Loss"
          checked={!!stopLoss}
        />

        {/* Stop Loss Percentage */}
        {stopLoss && (
          <>
            <TradingFormField
              type="checkbox"
              name="positionManagementConfig.trailingStop"
              label="Trailing Stop"
              checked={!!trailingStop}
            />
            {trailingStop && (
              <TradingFormField
                type="numeric"
                name="positionManagementConfig.trailingStopPercent"
                label="Trailing Stop Percentage"
              />
            )}
            <TradingFormField
              type="numeric"
              name="positionManagementConfig.stopLossPercent"
              label="Stop Loss Percentage"
            />
          </>
        )}

        {/* Scalping */}
        <TradingFormField
          type="checkbox"
          name="positionManagementConfig.scalping"
          label="Scalping"
          checked={!!scalping}
        />
        <TradingFormField
          type="checkbox"
          name="positionManagementConfig.scaleIn.enabled"
          label="Scale In"
          checked={isScaleInEnabled}
        />
        {isScaleInEnabled && (
          <>
            <TradingFormField
              type="numeric"
              name="positionManagementConfig.scaleIn.addSize"
              label="Add Size"
              inputProps={{ min: 0.000001, step: 0.1 }}
            />
            <TradingFormField
              type="numeric"
              name="positionManagementConfig.scaleIn.minPriceStepFromLastSignalPercent"
              label="Min Price Step From Last Signal (%)"
              inputProps={{ min: 0, step: 0.01 }}
            />
            <TradingFormField
              type="checkbox"
              name="positionManagementConfig.scaleIn.onlyInProfit"
              label="Only In Profit"
              checked={Boolean(scaleIn?.onlyInProfit)}
            />
          </>
        )}
      </FormContainer>
      {scalping && <TakeProfitLevelFields />}
    </>
  );
};

export const TakeProfitLevelFields: FC = () => {
  const { control, watch } = useTradingFormContext();
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'positionManagementConfig.takeProfitLevels'
  });

  const takeProfitLevels =
    watch('positionManagementConfig.takeProfitLevels') || [];

  useEffect(() => {
    if (!takeProfitLevels || takeProfitLevels.length === 0) {
      if (fields.length > 0) replace([]);
      return;
    }

    // Comprehensive comparison
    const needsSync =
      fields.length !== takeProfitLevels.length ||
      fields.some((_, index) => {
        const fieldValues = fields[index];
        const formValues = takeProfitLevels[index];

        return (
          !formValues ||
          fieldValues.threshold !== formValues.threshold ||
          fieldValues.positionPercent !== formValues.positionPercent
        );
      });

    if (needsSync) {
      replace(takeProfitLevels);
    }
  }, [takeProfitLevels, fields, replace]);

  return (
    <BaseWrapper sx={{ width: '100%', mt: 1.5 }}>
      <Section
        sx={{
          ...sectionPanelStyle,
          ...borderStyle
        }}
      >
        {fields.map((field, index) => {
          return (
            <Section key={'panel_' + field.id} sx={sectionHeaderPanelStyle}>
              <FormArrayItem key={field.id} index={index} onRemove={remove}>
                <FormContainer>
                  <TradingFormField
                    type="numeric"
                    name={`positionManagementConfig.takeProfitLevels.${index}.threshold`}
                    label="Threshold %"
                  />
                  <TradingFormField
                    type="numeric"
                    name={`positionManagementConfig.takeProfitLevels.${index}.positionPercent`}
                    label="Amount %"
                  />
                </FormContainer>
              </FormArrayItem>
            </Section>
          );
        })}
        <AppIconButton
          tone="primary"
          title="Add Profit Target"
          aria-label="Add Profit Target"
          onClick={() => append({} as TakeProfitLevelInput)}
        >
          <AddIcon />
        </AppIconButton>
      </Section>
    </BaseWrapper>
  );
};
